import axios, { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Appointment, FormFieldValues, SubmissionError, TimeSlotEntry } from '../types/booking';
import { ApiData, SubmitErrorResponse } from '../types/rest';
import { getLocalHour } from '../utils/dates';
import { formatAppointmentResponse } from '../utils/format';
import { useAnalytics } from './useAnalytics';
import { useAppointmentStorage } from './useAppointmentStorage';

interface UseAppointmentProps {
	availabilities: TimeSlotEntry[];
	bookingBusinessId: string;
	bookingServiceId: string;
	cancelAppointmentUrl: string;
	fetchAvailabilities: (callback?: (timeSlots: TimeSlotEntry[]) => void) => Promise<void>;
	generateRecaptchaToken: (action: string) => Promise<string>;
	getAppointmentUrl: string;
	settings: ApiData['settings'];
	submitAppointmentUrl: string;
	timezone: string;
}

const timeSlotsAreEqual = (a: TimeSlotEntry | null, b: TimeSlotEntry | null): boolean => {
	if (a === null || b === null) {
		return a === b;
	}

	return a.start_datetime === b.start_datetime && a.end_datetime === b.end_datetime && a.staff_id === b.staff_id;
};

export const useAppointment = ({
	availabilities,
	bookingBusinessId,
	bookingServiceId,
	cancelAppointmentUrl,
	fetchAvailabilities,
	generateRecaptchaToken,
	getAppointmentUrl,
	settings,
	submitAppointmentUrl,
	timezone,
}: UseAppointmentProps) => {
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [timeSlot, setTimeSlot] = useState<TimeSlotEntry | null>(null);
	const tempTimeSlot = useRef(timeSlot);

	// Submission
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [submissionError, setSubmissionError] = useState<SubmissionError | null>(null);
	const [appointment, setAppointment] = useState<Appointment | null>(null);
	const [appointmentSource, setAppointmentSource] = useState<'stored' | 'created' | null>(null);

	// Cancellation
	const [pendingCancellation, setPendingCancellation] = useState<boolean>(false);
	const [isCancelling, setIsCancelling] = useState<boolean>(false);

	// Analytics
	const {
		gtmEventEditAppointment,
		gtmEventChangeAppointment,
		gtmEventSubmissionError,
		gtmEventTimeSlotExpired,
		gtmEventCancelAppointment,
		gtmEventCancelAppointmentError,
		dispatchWindowBookedEvent,
	} = useAnalytics(bookingBusinessId, bookingServiceId);

	// Appointment Storage
	const { removeStoredAppointment, storeAppointment } = useAppointmentStorage({
		bookingBusinessId,
		bookingServiceId,
		getAppointmentUrl,
		setAppointment,
		setAppointmentSource,
	});

	// Determine if the selected time slot has expired
	const timeSlotExpired = useMemo(() => {
		return timeSlot ? !availabilities.find((availability) => timeSlotsAreEqual(availability, timeSlot)) : false;
	}, [timeSlot, availabilities]);

	const canEditAppointment = useMemo(() => {
		return availabilities.length > 1 && !isSubmitting;
	}, [availabilities.length, isSubmitting]);

	const editAppointment = useCallback(() => {
		setSubmissionError(null);

		// Store the current time slot in a temp ref
		tempTimeSlot.current = timeSlot;

		gtmEventEditAppointment();
		setIsEditing(true);
	}, [timeSlot, gtmEventEditAppointment]);

	const saveAppointment = useCallback(() => {
		if (!timeSlotsAreEqual(tempTimeSlot.current, timeSlot)) {
			gtmEventChangeAppointment();
		}

		setIsEditing(false);
	}, [timeSlot, gtmEventChangeAppointment]);

	const handleSubmissionError = useCallback(
		(axiosError: AxiosError<SubmitErrorResponse>) => {
			if (!axiosError.response?.data) {
				gtmEventSubmissionError('network_error');
				setSubmissionError({ message: 'Network error. Please check your connection and try again.' });
				return;
			}

			const { code, message, data: errorData } = axiosError.response.data;
			gtmEventSubmissionError(code);

			switch (code) {
				case 'form_field_errors':
					if (errorData?.errors) {
						const fieldErrors: Record<string, string> = {};
						errorData.errors.forEach((item) => {
							fieldErrors[item.field] = item.error;
						});
						return { fieldErrors };
					}
					break;
				case 'time_slot_unavailable':
					setSubmissionError({ message, detail: errorData?.detail || '' });
					setTimeSlot(null);
					fetchAvailabilities((timeSlots) => {
						if (timeSlots.length > 0) {
							setTimeSlot(timeSlots[0]); // Select the first available time slot
						}
					}); // Pass the callback
					break;
				default:
					setSubmissionError({ message, detail: errorData?.detail || '' });
			}
			return null;
		},
		[gtmEventSubmissionError, fetchAvailabilities],
	);

	const submitAppointment = useCallback(
		async (formFields: FormFieldValues) => {
			if (!timeSlot) return { success: false };

			setIsSubmitting(true);

			try {
				const recaptchaToken = await generateRecaptchaToken('submit_booking_appointment');

				const response = await axios.post(submitAppointmentUrl, {
					booking_business_id: bookingBusinessId,
					booking_service_id: bookingServiceId,
					form_fields: formFields,
					start_datetime: timeSlot.start_datetime,
					end_datetime: timeSlot.end_datetime,
					staff_id: timeSlot.staff_id,
					timezone,
					recaptcha_token: recaptchaToken,
				});

				setSubmissionError(null);

				setAppointment(formatAppointmentResponse(response.data));
				setAppointmentSource('created');

				// Store appointment
				storeAppointment(bookingBusinessId, bookingServiceId, response.data.id);

				dispatchWindowBookedEvent({ formFields });

				return { success: true, appointmentId: response.data.id };
			} catch (error) {
				if (axios.isAxiosError<SubmitErrorResponse>(error)) {
					const errorResult = handleSubmissionError(error);
					if (errorResult?.fieldErrors) {
						return { success: false, fieldErrors: errorResult.fieldErrors };
					}
				} else {
					gtmEventSubmissionError('unknown_error');
					setSubmissionError({ message: 'An unexpected error occurred.' });
				}
				return { success: false };
			} finally {
				setIsSubmitting(false);
			}
		},
		[
			bookingBusinessId,
			bookingServiceId,
			submitAppointmentUrl,
			generateRecaptchaToken,
			gtmEventSubmissionError,
			handleSubmissionError,
			timeSlot,
			timezone,
			storeAppointment,
			dispatchWindowBookedEvent,
		],
	);

	const abortCancellation = useCallback(() => {
		setPendingCancellation(false);
	}, []);

	const cancelAppointment = useCallback(async () => {
		if (!appointment) return false;

		if (!pendingCancellation) {
			setPendingCancellation(true);
			return false;
		}

		setIsCancelling(true);

		try {
			await axios.post(cancelAppointmentUrl, {
				booking_business_id: bookingBusinessId,
				booking_appointment_id: appointment.id,
			});

			// Send analytics event
			gtmEventCancelAppointment();

			// Delete stored appointment
			removeStoredAppointment(appointment.id);

			return true;
		} catch (error) {
			console.error('Failed to cancel appointment', error);
			gtmEventCancelAppointmentError();
			return false;
		} finally {
			setIsCancelling(false);
		}
	}, [
		appointment,
		bookingBusinessId,
		cancelAppointmentUrl,
		gtmEventCancelAppointment,
		gtmEventCancelAppointmentError,
		pendingCancellation,
		removeStoredAppointment,
	]);

	const clearAppointment = useCallback(() => {
		setPendingCancellation(false);
		setAppointment(null);
		setAppointmentSource(null);
	}, []);

	useEffect(() => {
		if (!isEditing && !timeSlot && availabilities.length > 0) {
			// Select the first suitable time slot
			const timeSlotAfterMinHour = availabilities.find((slot) => {
				const hour = getLocalHour(slot.start_datetime, timezone);
				return hour >= settings.min_preferred_hour;
			});

			setTimeSlot(timeSlotAfterMinHour ?? availabilities[0]);
		}
	}, [availabilities, isEditing, timeSlot, settings.min_preferred_hour, timezone]);

	useEffect(() => {
		if (!isEditing && timeSlotExpired) gtmEventTimeSlotExpired();
	}, [isEditing, timeSlotExpired, gtmEventTimeSlotExpired]);

	return {
		abortCancellation,
		appointment,
		appointmentSource,
		cancelAppointment,
		canEditAppointment,
		clearAppointment,
		editAppointment,
		isCancelling,
		isEditing,
		isSubmitting,
		pendingCancellation,
		saveAppointment,
		setTimeSlot,
		submissionError,
		submitAppointment,
		timeSlot,
		timeSlotExpired,
	};
};
